import React, {useEffect, useState} from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';

import ListGroup from 'react-bootstrap/ListGroup';
import Container from 'react-bootstrap/Container';
import Card from 'react-bootstrap/Card';
import Table from 'react-bootstrap/Table';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import InputGroup from 'react-bootstrap/InputGroup';
import FormControl from 'react-bootstrap/FormControl';
import Form from 'react-bootstrap/Form';

import {dateToDE, orderByName} from '../util/Util.js';
import * as validation from '../util/Forms.js';
import {emailAddress, genders, lookupGenderKeyFromShort, makeGenderSelection, makeInput} from '../util/Forms.js';
import useSubmitCancel from "../util/useSubmitCancel";


/*
Renders a Team with options for editing
 */
const TeamEditor = ({
	team,
	updateTeam,
	handleKeyEventIf,
}) => {
	const [tempMember, setTempMember] = useState();
	const [editIdx, setEditIdx] = useState(-1);
	const [tempFocusPerson, setTempFocusPerson] = useState();

	// enter- and escape-handling for single members
	const [submitRef, cancelRef, onKey] = useSubmitCancel();


	/*
		the focusperson problem
		- member list order is unstable
		- thus index isn't usable as identifier
		- ID isn't usable because new teammembers don't have one yet
		- workaround: allow only persisted members, ie. those with an ID, to be set as focus member

		implementation nastiness: it's a 2-level-editor: team and team-members
		- use team.focusPersonIdx as container for focusperson-ID while editing
		- createClientUpdate() checks that field for changes
	*/


	// focusperson
	const isFocusPerson = (member, idx) => {
		// temporary focusPerson while editing a member
		if (tempFocusPerson)
			return member.id === tempFocusPerson;

		// temporary focusPerson on the team
		if (team.focusPersonIdx !== undefined)
        	return team.focusPersonIdx === member.id;

		// if the team has a focusPerson: the member whose ID is set as the teams focusPerson
		return (team.focusPerson && team.focusPerson.id === member.id) ? true : false;
	};

	// remove

	const remove = idx => {
		const updatedTeam = {
			...team,
			members: team.members.filter((m, mIdx) => mIdx !== idx),
		};

		// if an existant member is removed check if it was focusPerson, and if so remove the teams' focusPerson
		if (team.focusPerson) {
			const removedId = team.members[idx].id;
			if (removedId && removedId === team.focusPerson.id)
				updatedTeam.focusPersonIdx = REMOVE_FOCUS_PERSON;
		}

		updateTeam(updatedTeam);
	};


	// cancel adding or editing

	const stopEditing = () => {
		setTempMember();
		setEditIdx(-1);
		setTempFocusPerson();
	};

	// when cancelling we need to remove the temporary focusPersonIdx from the team

	const cancel = () => {
		const teamWithoutFocusPersonRemoved = {...team};
		delete teamWithoutFocusPersonRemoved.focusPersonIdx;
		updateTeam(teamWithoutFocusPersonRemoved);

		stopEditing();
	};


	// when adding or updating a member check the temporary focusPerson

	const updateTeamWithFocusPerson = (updatedTeam, tempMemberCopy) => {
		if (tempFocusPerson) {
			updatedTeam.focusPersonIdx = tempFocusPerson;
			setTempFocusPerson();
		}

		updateTeam(updatedTeam);
		stopEditing();
	};


	// validation for editing and adding

	const validate = () => {
		const fieldsToValidate = ['name', 'surname', 'birthday', 'emailAddress'];
		const valid = fieldsToValidate
				.map(f => document.getElementById(f).validity.valid)
				.reduce((v1, v2) => v1 && v2);

		if (!valid)
			return false;

		return true;
	};


	// edit

	const edit = (member, idx) => {
		setTempMember({...member});
		setEditIdx(idx);
	};

	const updateMember = () => {
		if (!validate())
			return;

		const tempMemberCopy = {...tempMember};

		const updatedTeam = {
			...team,
			members: team.members.map((member, idx) => idx === editIdx ? tempMemberCopy : member),
		};

		updateTeamWithFocusPerson(updatedTeam, tempMemberCopy);
	}


	// add

	const startAdd = () => {
		setTempMember({
			name: '',
			surname: '',
			birthday: '',
			function: '',
			gender: 'MALE',
		});
		setEditIdx(team.members.length);
	};

	const addMember = () => {
		if (!validate())
			return;

		const tempMemberCopy = {...tempMember};

		const updatedTeam = {
			...team,
			members: team.members.concat(tempMemberCopy),
		};

		updateTeamWithFocusPerson(updatedTeam, tempMemberCopy);
	}

	const onChangeMember = ev => setTempMember({
		...tempMember,
		[ev.target.name]: ev.target.value,
	});


	// cards for members with edit- and delete-buttons
	const memberToCard2 = (member, idx) => {
		const rows = teamMemberFields
			// exclude name and surname
			.filter(f => f.fieldname !== 'name' && f.fieldname !== 'surname')
			// map the birthdate to DE-typical format
			.map(f => f.fieldname === 'birthday' ? ({
				...f,
				extract: m => dateToDE(m.birthday)
			}) : f)
			// map the gender to it's label
			.map(f => f.fieldname === 'gender' ? ({
				...f,
				extract: m => genders[m.gender].label,
			}) : f)
			.map(f => makeMemberListGroupItem(member, f));
		rows.push(
			<ListGroup.Item
				className = {'px-0'}
				key = {'focus-person'}>
				<Container>
					<Row>
						<Col className = {'col-4'}>
							<b>{'Fokus-Person:'}</b>
						</Col>
						<Col>
							<input
								type = {'checkbox'}
								checked = {isFocusPerson(member, idx)}
								readOnly = {true}/>
						</Col>
					</Row>
				</Container>
			</ListGroup.Item>
		)

		return (
			<Card
				className = {'mb-4'}
				key = {idx}>
				<Card.Header>
					<Container className = {'px-0'}>
						<Row className = {'align-items-center'}>
							<Col>
								{`${member.surname}, ${member.name}`}
							</Col>
							<Col
								className = {tempMember ? 'invisible' : ''}
								xs = {'auto'}>
								<Button className = {'mr-1'}
									onClick = {() => edit(member, idx)}>
									<i className = {'bi-pencil'}/>
								</Button>
								<Button className = {'btn-danger ml-1'}
									onClick = {() => remove(idx)}>
									<i className = {'bi-x-lg'}/>
								</Button>
							</Col>
						</Row>
					</Container>
				</Card.Header>
				<ListGroup variant = 'flush'>
					{rows}
				</ListGroup>
			</Card>
		);
	};


	// card for editing
	const memberToEditableListGroup2 = (member, idx, executeButton) => {
		// add ID for validation
		const rows = teamMemberFields.map(f => makeInput({
			...f,
			id: f.fieldname,
		}, member, onChangeMember));


		// focusPerson checkbox only for members that are already persisted
		if (member.id) {
			rows.push(
				<InputGroup
					key = {'focusperson-check'}>
					<InputGroup.Text>
						{'Fokus-Person'}
					</InputGroup.Text>
					<Form.Control
						name = {'focusPerson-check'}
						onChange = {ev => setTempFocusPerson(ev.target.checked ? member.id : REMOVE_FOCUS_PERSON)}
						type = {'checkbox'}
						checked = {isFocusPerson(member, idx)}/>
				</InputGroup>
			);
		}

		return (
			<Card
				onKeyUp = {onKey}
				key = {'editedMember'}
				className={'mb-4'}>
				<Card.Header>
					<Container className = {'px-0'}>
						<Row className = {'align-items-center'}>
							<Col>
								{`${member.surname}, ${member.name}`}
							</Col>
							<Col
								xs = {'auto'}>
								{executeButton}
								<Button className = {'ml-1'}
										ref = {cancelRef}
										onClick = {cancel}>
									<i className = {'bi-x-lg'}/>
								</Button>
							</Col>
						</Row>
					</Container>
				</Card.Header>
				{rows}
			</Card>
		);
	}


	const makeEditButton = () => (
		<Button className = {'mr-1'}
				ref = {submitRef}
				onClick = {updateMember}>
			<i className = {'bi-check-lg'}/>
		</Button>
	);

	// entry creation: editable or readonly-card
	const makeEntry = (member, idx) => idx === editIdx ?
		memberToEditableListGroup2(member, idx, makeEditButton()) : memberToCard2(member, idx);


	// team members

	const makeEntries = members => {
		// replace edited entry with tempMember
		const entries = members
			.map((member, idx) => idx === editIdx ? tempMember : member)
			.map(makeEntry);

		// if adding append tempMember
		if (editIdx === team.members.length) {
			const addMemberButton = (
				<Button className = {'btn-success'}
					ref = {submitRef}
					onClick = {addMember}>
					<i className = {'bi-check-lg'}/>
				</Button>
			);
			entries.push(memberToEditableListGroup2(tempMember, editIdx, addMemberButton));
		}

		// wrap for container
		return entries
			.map((e, idx) => (
				<Col
					className = {'col-4'}
					key = {`member-${idx}`}>
					{e}
				</Col>
			));;
	};


	// CSV editor

	const [showCsvEditor, setShowCsvEditor] = useState();
	const addMembersFromCsv = members => {
		setShowCsvEditor(false);

		const updatedTeam = {
			...team,
			members: team.members.concat(members),
		};

		updateTeamWithFocusPerson(updatedTeam);
	};

	// prevent enter and escape events from triggering update / cancel on the team editor while the CSV editor is open
	useEffect(() => {
		if (handleKeyEventIf) {
			handleKeyEventIf(ev => {
				if (showCsvEditor) {
					ev.preventDefault();
					ev.stopPropagation();
					return false;
				}

				return true;
			});
		}

		return () => {
			if (handleKeyEventIf)
				handleKeyEventIf();
		};
	}, [showCsvEditor]);


	// team name

	const updateTeamName = ev => updateTeam({
		...team,
		name: ev.target.value
	});

	const teamName = (
		<Col>
			<Form.Group>
				<InputGroup
					hasValidation
					key = {'teamname'}>
					<InputGroup.Text>
						{'Teamname'}
					</InputGroup.Text>
					<FormControl
						required = {true}
						name = {'teamname'}
						onChange = {updateTeamName}
						value = {team.name}/>
				</InputGroup>
			</Form.Group>
		</Col>
	);


	// company

	const updateCompany = ev => updateTeam({
		...team,
		company: ev.target.value
	});
	const company = (
		<Col>
			<Form.Group>
				<InputGroup
					hasValidation
					key = {'company'}>
					<InputGroup.Text>
						{'Firma'}
					</InputGroup.Text>
					<FormControl
						name = {'company'}
						onChange = {updateCompany}
						value = {team.company || ''}/>
				</InputGroup>
			</Form.Group>
		</Col>
	);


	// render

	return (
		<div>
			<Row className = {'mb-2'}>
				{teamName}
			</Row>
			<Row className = {'mb-2'}>
				{company}
			</Row>
			<Row className = {'mb-2'}>
				<Col>
					{(
						<Row>
							{makeEntries(team.members)}
						</Row>
					)}
				</Col>
			</Row>
			{updateTeam &&
				<>
					<Row>
						<Col>
							{!tempMember &&
								<>
									<Button className = {'mr-1'}
										onClick = {startAdd}>
										{'Neues Teammitglied'}
									</Button>
									<Button className = {'ml-1'}
										onClick = {() => setShowCsvEditor(true)}>
										{'CSV-Eingabe'}
									</Button>
								</>
							}
						</Col>
					</Row>
					{showCsvEditor &&
						<Modal
							show = {true}
							size = {'lg'}
							centered
							backdrop = {'static'}
							keyboard = {false}
							onHide = {() => setShowCsvEditor(false)}>
							<Container>
								<CSVEditor
									team = {team}
									addMembers = {addMembersFromCsv}
									cancel = {() => setShowCsvEditor(false)}/>
							</Container>
						</Modal>
					}
				</>
			}
		</div>
	);
};

export default TeamEditor;

const CSVEditor = ({
	addMembers,
	cancel,
}) => {
	const [csv, setCsv] = useState();
	const [members, setMembers] = useState();

	const cancelCsv = ev => {
		ev.preventDefault();
		cancel();
	}

	const [validCsv, setValidCsv] = useState();

	const updateCsv = ev => {
		ev.preventDefault();

		const text = ev.target.value;
		setCsv(text);

		// parse members from CSV and update state
		let valid = false;
		const validMembers = [];

		// find lines, trim them and throw away empty ones
		const lines = text.split('\n')
			.map(l => l.trim())
			.filter(l => l.length);

		let parseLine = line => {
			// if we get an error at any point: report failure
			try {
				// split on '/'
				const parts = line.split('/');

				/*
                valid formats:
                - Nachname/Vorname/[m|w|d]/Geburtsdatum/email
                - Nachname/Vorname/[m|w|d]/Geburtsdatum/email/Funktion
                 */

				if (parts.length < 5 || parts.length > 6)
					return false;

				// surname, name
				const surname = parts[0];
				const name = parts[1];

				for (const n of [surname, name])
					if (n.length === 0)
						return false;

				// gender
				const genderString = parts[2].toLowerCase();
				if (!new RegExp('[mwd]').test(genderString))
					return false;

				// birthdate
				const birthdateParts = parts[3].split('.');

				const year = birthdateParts[2];
				let month = birthdateParts[1];
				// single-digit month is parsed wrong
				if (month.length === 1)
					month = '0' + month;
				let day = birthdateParts[0];
				if (day.length === 1)
					day = '0' + day;

				// make sure that the birthdate is valid
				const birthdate = `${year}-${month}-${day}`;
				if (new Date(birthdate).toString() === 'Invalid Date')
					return false;

				// email
				const email = parts[4];
				if (!(new RegExp(emailAddress.pattern).test(email)))
					return false;

				// function: optional part, thus empty is acceptable
				const func = parts[5] || '';

				// enter parsed member into list of valid members
				return ({
					name: name,
					surname: surname,
					birthday: birthdate,
					emailAddress: email,
					function: func,
					gender: lookupGenderKeyFromShort(genderString),
				});
			}
			catch (e) {
				return false;
			}
		};

		for (const line of lines) {
			const member = parseLine(line);
			valid = member !== false;
			if (member)
				validMembers.push(member);
			else
				break;
		}

		setMembers(valid ? validMembers : null);
		setValidCsv(valid);
	};

	const updateTeam = ev => {
		ev.preventDefault();

		addMembers(members);
	};


	// render

	return (
		<div className = {'m-3'}>
			<Row>
				<Col>
					<h5>
						Gültiges Eingabeformat:
					</h5>
					<ul>
						<li>Nachname/Vorname/Geschlecht/Geburtsdatum/E-Mail Adresse (/Funktion)</li>
						<li>Geschlecht: m männlich, w weiblich, d divers</li>
						<li>zB. Mustermann/Max/m/15.01.2001/max@mustermail.de/Angestellter</li>
					</ul>
				</Col>
			</Row>
			<Row>
				<Col>
					<textarea
						className = {'w-100 csv-editor'}
						value = {csv}
						onChange = {updateCsv}/>
				</Col>
			</Row>
			<Row>
				<Col>
					<Button className = {'mr-1'}
						disabled = {!validCsv}
						onClick = {updateTeam}>
						{'Übernehmen'}
					</Button>
					<Button className = {'ml-1'}
						onClick = {cancelCsv}>
						{'Abbrechen'}
					</Button>
				</Col>
			</Row>
		</div>
	);
};

const REMOVE_FOCUS_PERSON = -2;

// TODO integrate focus-person in teamMemberFields
export const teamMemberFields = [
	{
		label: 'Vorname',
		fieldname: 'name',
		required: true,
		pattern: validation.name.pattern,
		validationMessage: validation.name.message,
	},
	{
		label: 'Nachname',
		fieldname: 'surname',
		required: true,
		pattern: validation.surname.pattern,
		validationMessage: validation.surname.message,
	},
	{
		label: 'Email-Adresse',
		fieldname: 'emailAddress',
		required: true,
		pattern: validation.emailAddress.pattern,
		validationMessage: validation.emailAddress.message,
	},
	{
		label: 'Geb.',
		fieldname: 'birthday',
		required: true,
		type: 'date',
	},
	makeGenderSelection(m => m.gender),
	{
		label: 'Funktion',
		fieldname: 'function',
	},
];

const makeMemberListGroupItem = (m, {label, fieldname, extract = m => m[fieldname]}) => (
	<ListGroup.Item
		className = {'px-0'}
		key = {fieldname}>
		<Container>
			<Row>
				<Col className = 'col-4'>
					<b>{`${label}:`}</b>
				</Col>
				<Col>
					{extract(m) || ''}
				</Col>
			</Row>
		</Container>
	</ListGroup.Item>
);

// tabular representation

const TeamHeader = ({
	team,
}) => {
	const teamName = (
		<>
			<Col className = {'col-4'}>
				<b>{'Teamname:'}</b>
			</Col>
			<Col>
				{team.name}
			</Col>
		</>
	);

	const company = (
		<>
			<Col className = {'col-4'}>
				<b>{'Firma:'}</b>
			</Col>
			<Col>
				{team.company}
			</Col>
		</>
	);

	return (
		<>
			<Row>
				{teamName}
			</Row>
			<Row className = {'mb-2'}>
				{company}
			</Row>
		</>
	)
};

export const TeamTable = ({
	team,
}) => (
	<>
		<TeamHeader
			team = {team}/>
		<Row>
			<Col>
				<TeamMemberTable
					team = {team}/>
			</Col>
		</Row>
	</>
);

const TeamMemberTable = ({
	team,
}) => {
	const makeEntries = members => members.map(member => {
		const isFocusPerson = team.focusPerson && team.focusPerson.id === member.id;
		const surname = isFocusPerson ? <b>{member.surname}</b> : member.surname;
		const name = isFocusPerson ? <b>{member.name}</b> : member.name;

		return (
			<tr key = {member.id}>
				{[	surname,
					name,
					genders[member.gender].label,
					dateToDE(member.birthday),
					member.function]
					.map((val, idx) =>
						<td className = {'align-middle'}
							key = {`${member.id}_${idx}`}>
							{val}
						</td>)}
			</tr>
		);
	});

	const entries = makeEntries([...team.members].sort(orderByName));

	return (
		<Table
			className = {'client-team-table'}
			bordered
			size = 'sm'>
			<thead>
			<tr>
				<th className = {'surname'}>{'Nachname'}</th>
				<th className = {'name'}>{'Vorname'}</th>
				<th className = {'gender'}>{'Geschlecht'}</th>
				<th className = {'birthday'}>{'Geburtsdatum'}</th>
				<th className = {'function'}>{'Funktion'}</th>
			</tr>
			</thead>
			<tbody>
			{entries}
			</tbody>
		</Table>
	);
};
